import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Container, CircularProgress, Alert, Fade } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import useSalesAnalytics from '../hooks/useSalesAnalytics';
import useMySubOrders from '../hooks/useMySubOrders';
import SalesFilter from '../components/sales-analytics/SalesFilter';
import SalesSummaryCards from '../components/sales-analytics/SalesSummaryCards';
import SalesOrdersTable from '../components/sales-analytics/SalesOrdersTable';
import SalesDailyTable from '../components/sales-analytics/SalesDailyTable';
import SalesLeaderboardTable from '../components/sales-analytics/SalesLeaderboardTable';
import MySubOrdersTable from '../components/sales-analytics/MySubOrdersTable';
import MySubOrderDetail from '../components/sales-analytics/MySubOrderDetail';
import Pagination from '../components/common/Pagination';
import userService from '../services/userService';
import useAuth from '../hooks/useAuth';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import { useTranslation } from 'react-i18next';
import { todayIST } from '../utils/dates';

const DEFAULT_PAGE_LIMIT = 50;

const getDefaultDateRange = () => {
    const today = todayIST();
    return { from: today, to: today };
};

const SalesStaffPage = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { userRole } = useRole();
    const isSalesStaff = userRole === USER_ROLES.SALES_STAFF;
    const isAdminOrSuper = userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN;
    const isOperationsManager = userRole === USER_ROLES.OPERATIONS_MANAGER;

    const TABS = React.useMemo(() => {
        const tabs = [
            { id: 'summary',     label: t('salesAnalytics.tabs.summary') },
            { id: 'orders',      label: t('salesAnalytics.tabs.orders') },
            { id: 'daily',       label: t('salesAnalytics.tabs.dailyTrend') },
            { id: 'leaderboard', label: t('salesAnalytics.tabs.leaderboard') },
        ];
        if (isSalesStaff || isAdminOrSuper || isOperationsManager) {
            tabs.push({ id: 'deliveries', label: t('salesAnalytics.tabs.deliveries') });
        }
        return tabs;
    }, [t, isSalesStaff, isAdminOrSuper, isOperationsManager]);

    const {
        summary, orders, ordersTotal,
        dailyData, leaderboard,
        fetching, error,
        fetchSummary, fetchOrders, fetchDaily, fetchLeaderboard,
    } = useSalesAnalytics();

    const {
        items: mySubOrders,
        total: mySubOrdersTotal,
        loading: mySubOrdersLoading,
        detail: mySubOrderDetail,
        detailLoading: mySubOrderDetailLoading,
        fetchList: fetchMySubOrders,
        fetchDetail: fetchMySubOrderDetail,
        clearDetail: clearMySubOrderDetail,
    } = useMySubOrders();

    const DELIVERY_PAGE_LIMIT = 20;

    const [selectedSubOrder, setSelectedSubOrder] = useState(null);

    const [salesStaff, setSalesStaff] = useState([]);
    const [salesStaffLoading, setSalesStaffLoading] = useState(false);
    const [selectedStaffId, setSelectedStaffId] = useState('');

    const [dateRange, setDateRange] = useState(getDefaultDateRange);
    const [activeTab, setActiveTab] = useState('summary');
    const [status, setStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const isTabEffectReady = useRef(false);

    const filterRef = useRef({});
    filterRef.current = { selectedStaffId, dateRange, status };

    const buildBaseParams = useCallback(() => {
        const { selectedStaffId: sid, dateRange: dr } = filterRef.current;
        const p = { from_date: dr.from, to_date: dr.to };
        if (sid) p.sales_staff_id = sid;
        return p;
    }, []);

    const fetchForTab = useCallback(async (tab, page = 1) => {
        const base = buildBaseParams();
        const { status: sf } = filterRef.current;
        const skip = (page - 1) * DEFAULT_PAGE_LIMIT;

        if (tab === 'summary') {
            await fetchSummary(base);
        } else if (tab === 'orders') {
            const p = { ...base, limit: DEFAULT_PAGE_LIMIT, skip };
            if (sf) p.status = sf;
            await fetchOrders(p);
        } else if (tab === 'daily') {
            await fetchDaily(base);
        } else if (tab === 'leaderboard') {
            // leaderboard: no sales_staff_id filter, only date range
            await fetchLeaderboard({ from_date: base.from_date, to_date: base.to_date });
        } else if (tab === 'deliveries') {
            const dParams = { ...base, skip, limit: DELIVERY_PAGE_LIMIT };
            if (sf) dParams.status = sf;
            await fetchMySubOrders(dParams);
        }
    }, [buildBaseParams, fetchSummary, fetchOrders, fetchDaily, fetchLeaderboard, fetchMySubOrders]);

    // Load staff list + initial summary on mount
    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            setSalesStaffLoading(true);
            try {
                let initialId = '';
                if (isSalesStaff && user?.id) {
                    initialId = String(user.id);
                    setSelectedStaffId(initialId);
                } else if (isAdminOrSuper) {
                    const res = await userService.getUsers({ role: 'SALES_STAFF', limit: 200 });
                    if (cancelled) return;
                    const list = res?.success ? (Array.isArray(res.data) ? res.data : res.data?.users ?? []) : [];
                    setSalesStaff(list);
                }

                const dr = getDefaultDateRange();
                const base = { from_date: dr.from, to_date: dr.to };
                if (initialId) base.sales_staff_id = initialId;
                await fetchSummary(base);
            } finally {
                if (!cancelled) {
                    setSalesStaffLoading(false);
                    isTabEffectReady.current = true;
                }
            }
        };
        init();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-fetch when tab changes
    useEffect(() => {
        if (!isTabEffectReady.current) return;
        setCurrentPage(1);
        fetchForTab(activeTab, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Re-fetch on pagination change
    useEffect(() => {
        if (!isTabEffectReady.current) return;
        if (activeTab === 'orders' || activeTab === 'deliveries') {
            fetchForTab(activeTab, currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const handleApply = () => {
        setCurrentPage(1);
        fetchForTab(activeTab, 1);
    };

    const totalPages = Math.ceil(ordersTotal / DEFAULT_PAGE_LIMIT) || 1;
    const showPagination = activeTab === 'orders';

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            {/* Header */}
            <Box mb={5}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Box sx={{
                        bgcolor: '#16a34a', borderRadius: 2,
                        width: 40, height: 40,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white',
                    }}>
                        <FontAwesomeIcon icon={faChartLine} />
                    </Box>
                    <Typography variant="h4" fontWeight="800" letterSpacing="-0.5px">
                        Sales Staff
                    </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700 }}>
                    Analyze sales order performance, staff leaderboard, daily trends, and dispatched weight.
                    Warehouse is selected from the sidebar.
                </Typography>
            </Box>

            {/* Filter bar */}
            <SalesFilter
                salesStaff={salesStaff}
                salesStaffLoading={salesStaffLoading}
                selectedStaffId={selectedStaffId}
                onStaffChange={setSelectedStaffId}
                fromDate={dateRange.from}
                toDate={dateRange.to}
                onFromDateChange={(v) => setDateRange((p) => ({ ...p, from: v }))}
                onToDateChange={(v) => setDateRange((p) => ({ ...p, to: v }))}
                status={status}
                onStatusChange={setStatus}
                onSubmit={handleApply}
                loading={fetching}
                activeTab={activeTab}
                disableStaffSelector={isSalesStaff}
            />

            {/* Tabs */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, borderBottom: '2px solid #f3f4f6', flexWrap: 'wrap' }}>
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            padding: '10px 20px',
                            border: 'none',
                            borderBottom: activeTab === tab.id ? '3px solid #16a34a' : '3px solid transparent',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: activeTab === tab.id ? 700 : 500,
                            color: activeTab === tab.id ? '#16a34a' : '#6b7280',
                            transition: 'all 0.15s',
                            marginBottom: -2,
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </Box>

            {/* Error */}
            {error && (
                <Fade in>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {typeof error === 'object' ? JSON.stringify(error) : error}
                    </Alert>
                </Fade>
            )}

            {/* Loading spinner for initial summary load */}
            {fetching && activeTab === 'summary' && !summary && (
                <Box display="flex" justifyContent="center" p={10}>
                    <CircularProgress size={40} thickness={4} sx={{ color: '#16a34a' }} />
                </Box>
            )}

            {/* Tab content */}
            {activeTab === 'summary' && (
                <Fade in={!!summary}>
                    <div><SalesSummaryCards summary={summary} /></div>
                </Fade>
            )}

            {activeTab === 'orders' && (
                <>
                    <SalesOrdersTable items={orders} loading={fetching} />
                    {showPagination && orders?.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={ordersTotal}
                            onPageChange={setCurrentPage}
                            loading={fetching}
                            totalLabel={t('salesAnalytics.pagination.totalLabels.orders')}
                            pageSize={DEFAULT_PAGE_LIMIT}
                        />
                    )}
                </>
            )}

            {activeTab === 'daily' && (
                <SalesDailyTable items={dailyData} loading={fetching} />
            )}

            {activeTab === 'leaderboard' && (
                <SalesLeaderboardTable items={leaderboard} loading={fetching} />
            )}

            {activeTab === 'deliveries' && (
                <>
                    <MySubOrdersTable
                        items={mySubOrders}
                        loading={mySubOrdersLoading}
                        onSelectOrder={(row) => {
                            setSelectedSubOrder(row);
                            fetchMySubOrderDetail(row.id);
                        }}
                    />
                    {mySubOrdersTotal > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(mySubOrdersTotal / DELIVERY_PAGE_LIMIT) || 1}
                            totalCount={mySubOrdersTotal}
                            onPageChange={setCurrentPage}
                            loading={mySubOrdersLoading}
                            totalLabel={t('salesAnalytics.pagination.totalLabels.subOrders')}
                            pageSize={DELIVERY_PAGE_LIMIT}
                        />
                    )}
                </>
            )}

            {selectedSubOrder && (
                <MySubOrderDetail
                    row={selectedSubOrder}
                    detail={mySubOrderDetail}
                    loading={mySubOrderDetailLoading}
                    onClose={() => {
                        setSelectedSubOrder(null);
                        clearMySubOrderDetail();
                    }}
                />
            )}
        </Container>
    );
};

export default SalesStaffPage;
